import useAnalytics from '../../hooks/useAnalytics';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useProject } from '../../config/ProjectContext';
import GraphqlService from '../../service/graphqlService';
import PageBanner from '../../components/utils/PageBanner';
import SimpleAlert from '../../components/utils/SimpleAlert';
import ProgressDialog from '../../components/utils/ProgressDialog';
import JSONBuilderFilters, { valueTypes } from '../../components/utils/JSONBuilderFilters';
import BrandObjectTable from './MachineTelemetry/BrandObjectTable';
import { validate, calculateEndDateTimeIso, calculateStartDateTimeIso } from '../../utils/dateUtils';
// import FineGrainedPermissionsInfo from '../Admin/FineGrainedPermissions/FineGrainedPermissionsInfo';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

const jsonColumns = [
  {
    id: 'created_on_local', label: 'Device Timestamp (Local)', valueType: valueTypes.DATE, values: [],
  },
  {
    id: 'created_on_utc', label: 'Device Timestamp (UTC)', valueType: valueTypes.DATE, values: [],
  },
  {
    id: 'device_serial', label: 'Device Serial', valueType: valueTypes.STRING, values: []
  },
  {
    id: 'country_code', label: 'Market', valueType: valueTypes.STRING, values: [],
  },
  {
    id: 'event_code', label: 'Event', valueType: valueTypes.STRING, values: [],
  },
  {
    id: 'extra', label: 'Extra', valueType: valueTypes.STRING, values: [],
  },
  {
    id: 'processed_on_utc', label: 'Platform Timestamp UTC', valueType: valueTypes.DATE, values: [],
  },
];

export default function BrandEvents() {
  const { setAnalytics } = useAnalytics();
  const MAX_DAYS_BETWEEN_DATES = 31;
  const params = { add_dict: true };
  const { project } = useProject();

  const [totalCount, setTotalCount] = useState(0);
  const [reducedPage, setReducedPage] = useState(0);
  const [, setPaginationTokens] = useState(['']);
  const [rowsPerPageConsumption] = useState(10);
  const [reducedResponse, setReducedResponse] = useState(null);
  const [useProcessedTimestamp, setUseProcessedTimestamp] = useState(false);
  const [noResultsDialogOpen, setNoResultsDialogOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertHeader, setAlertHeader] = useState('');
  const [alertText, setAlertText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setDateStartError] = useState(false);
  const [, setDateEndError] = useState(false);
  const [, setGreaterSmallerDateError] = useState(false);
  const [, setMoreThanMaxDaysDateError] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState('');
  const [selectedStartDate, setSelectedStartDate] = useState(moment().add(-7, 'days'));
  const [selectedStartTime, setSelectedStartTime] = useState(moment().format('12:00'));
  const [selectedEndDate, setSelectedEndDate] = useState(moment());
  const [selectedEndTime, setSelectedEndTime] = useState(moment().format('23:59'));
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [dateRangePickerVisibility, setDateRangePickerVisibility] = useState(true);
  const [filterError, setFilterError] = useState('');

  function openAlert(header, text) {
    setAlertHeader(header);
    setAlertText(text);
    setAlertOpen(true);
  }

  async function searchObject(newPage, useProcessedTime = useProcessedTimestamp) {
    const startDate = calculateStartDateTimeIso(selectedStartDate, selectedStartTime);
    const endDate = calculateEndDateTimeIso(selectedEndDate, selectedEndTime);
    setDateRangePickerVisibility(!dateRangePickerVisibility);

    const searchParams = { ...params, use_processed_timestamp: useProcessedTime };

    const consumptionResponse = await GraphqlService.getTelemetryObject(
      project.code, 'events', JSON.stringify(searchParams), selectedFilters, startDate, endDate, rowsPerPageConsumption, (newPage || 0) * rowsPerPageConsumption, MAX_DAYS_BETWEEN_DATES, useProcessedTimestamp,
    );

    if (consumptionResponse) {
      if (totalCount < 1 || !newPage) {
        setTotalCount(consumptionResponse.pagination.totalRows);
      }
      return JSON.parse(consumptionResponse.data);
    }
    return [];
  }

  async function search(newPage, useProcessedTime = useProcessedTimestamp) {
    try {
      setSubmitting(true);
      setProgressDialogOpen(true);
      setLoading(true);
      let creationResponse = [];

      setReducedPage(newPage || 0);
      creationResponse = await searchObject(newPage, useProcessedTime);
      setPaginationTokens(['']);
      setReducedResponse(creationResponse);

      if (creationResponse ? creationResponse.length === 0 && !useProcessedTime : false) {
        setNoResultsDialogOpen(true);
      }

      setMoreThanMaxDaysDateError(false);

      setSubmitting(false);
      setProgressDialogOpen(false);
      setLoading(false);
      return creationResponse;
    } catch (error) {
      setProgressDialogOpen(false);
      setSubmitting(false);
      setLoading(false);
      openAlert('Error', error.message);
      return null;
    }
  }

  const onSearch = (event) => {
    event.preventDefault();
    if (!validate(selectedStartDate, selectedEndDate, selectedStartTime, selectedEndTime, MAX_DAYS_BETWEEN_DATES, setDateStartError, setDateEndError, setSelectedStartDate, setSelectedStartTime, setSelectedEndDate, setSelectedEndTime, setGreaterSmallerDateError, setMoreThanMaxDaysDateError)) return;
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: 'device_management-events-search',
      },
    });
    search();
  };

  useEffect(() => {
    setNoResultsDialogOpen(false);
  }, [useProcessedTimestamp]);

  const handleRetryWithProcessedTimestamp = () => {
    const newEndDate = moment();
    const newStartDate = moment(newEndDate).subtract(3, 'days');

    setSelectedEndDate(newEndDate);
    setSelectedEndTime(newEndDate.format('23:59'));
    setSelectedStartDate(newStartDate);
    setSelectedStartTime(newStartDate.format('00:00'));

    setUseProcessedTimestamp(true);
    setNoResultsDialogOpen(false);

    setTimeout(() => {
      search(0, true);
    }, 100);
  };

  const handleKeepResults = () => {
    setNoResultsDialogOpen(false);
  };

  const transformData = (data) => data?.map((item) => {
    // Get all extra properties and concatenate them with semicolon
    const extraValues = [];
    if (item.extra && typeof item.extra === 'object') {
      Object.values(item.extra).forEach(value => {
        if (value !== null && value !== undefined && value !== '') {
          extraValues.push(String(value));
        }
      });
    }
    // Also check for flat extra properties
    Object.keys(item).forEach(key => {
      if (key.startsWith('extra.') && item[key] !== null && item[key] !== undefined && item[key] !== '') {
        extraValues.push(String(item[key]));
      }
    });
    const extraConcatenated = extraValues.join(' ; ');

    const eventDescription = item.dict_event?.extra?.description || '';
    const errorType = item['extra.error_type'] || '';

    let eventDetails = '';
    if (eventDescription && errorType) {
      eventDetails = `(${eventDescription}; ${errorType})`;
    } else if (eventDescription) {
      eventDetails = `(${eventDescription})`;
    } else if (errorType) {
      eventDetails = `(${errorType})`;
    }

    // Always show code, even if name is missing
    const eventCode = item.dict_event?.code || item.event_code || '';
    const eventName = item.dict_event?.name || '';
    let eventColumn;
    if (eventName) {
      eventColumn = `${eventCode} - ${eventName} ${eventDetails}`.trim();
    } else {
      eventColumn = `${eventCode} ${eventDetails}`.trim();
    }

    return {
      'Device Timestamp (Local)': item.created_on_local,
      'Device Timestamp (UTC)': item.created_on_utc,
      'Device Serial': item.device_serial,
      ...(item.module_serial ? { 'Module Serial': item.module_serial } : {}),
      Market: item.country_code,
      Event: eventColumn,
      Extra: extraConcatenated || '-',
      'Platform Timestamp (UTC)': item.processed_on_utc,
    };
  });

  const transformedData = transformData(reducedResponse);

  return (
    <div className="space-y-4">
      <ProgressDialog open={progressDialogOpen} setOpen={setProgressDialogOpen} header="Retrieving statistics, please wait" />
      <SimpleAlert open={alertOpen} setOpen={setAlertOpen} header={alertHeader} body={alertText} />
      <PageBanner title="DEVICE EVENTS" />

      {/* <FineGrainedPermissionsInfo open={false} onClose={() => { }} style={{ margin: 24 }} /> */}

      <div className="bg-gray-100 p-4 rounded-lg">
        <JSONBuilderFilters
          id="DeviceEvents"
          setSelectedFilters={setSelectedFilters}
          selectedFilters={selectedFilters}
          submitting={submitting}
          selectedStartDate={selectedStartDate}
          setSelectedStartDate={setSelectedStartDate}
          selectedStartTime={selectedStartTime}
          setSelectedStartTime={setSelectedStartTime}
          selectedEndDate={selectedEndDate}
          setSelectedEndDate={setSelectedEndDate}
          selectedEndTime={selectedEndTime}
          setSelectedEndTime={setSelectedEndTime}
          filtersVisible
          deviceSerialVisible={false}
          jsonColumns={jsonColumns}
          setDateRangePickerVisibility={dateRangePickerVisibility}
          onSearch={onSearch}
          setFilterError={setFilterError}
          filterError={filterError}
          useProcessedTimestamp={useProcessedTimestamp}
          setUseProcessedTimestamp={setUseProcessedTimestamp}
          showTimestampToggle
        />
      </div>

      {noResultsDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg space-y-4">
            <div className="flex items-center space-x-2">
              <ExclamationCircleIcon className="h-6 w-6 text-red-500" />
              <h2 className="text-lg font-semibold">No Results Found</h2>
            </div>
            <p>Your search was conducted using the machine&apos;s timestamp (created_on). Please note that there may be instances where this timestamp is incorrectly set, which could affect the search results.</p>
            <p>Would you like to perform the search using the processed timestamp (processed_on) instead? This will automatically adjust your search to the last 3 days.</p>
            <div className="flex justify-end space-x-2">
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                onClick={handleRetryWithProcessedTimestamp}
              >
                Yes
              </button>
              <button
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                onClick={handleKeepResults}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
        <BrandObjectTable
          search={(newPage) => search(newPage)}
          dataList={transformedData}
          totalCount={totalCount}
          filters={selectedFilters}
          loading={loading}
          dateIni={calculateStartDateTimeIso(selectedStartDate, selectedStartTime)}
          dateEnd={calculateEndDateTimeIso(selectedEndDate, selectedEndTime)}
          rangeDates={MAX_DAYS_BETWEEN_DATES}
          page={reducedPage}
          setPage={setReducedPage}
          rowsPerPage={rowsPerPageConsumption}
          object="events"
          params={params}
          id="DeviceEvents"
          setFilterError={setFilterError}
          filterError={filterError}
          disableOrderingHeader
          isOverview={false}
        />
      </div>
    </div>
  );
}
